/** @jsxImportSource @emotion/react */
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation} from "react-i18next";
import {Icon, Spinner} from "@blueprintjs/core";

// Libs
import Button from "../common/components/button";
import {primaryColor} from "../common/libs/theme-lib";
import moment from "moment";

// Components
import TextInput from "../common/components/text-input";
import RenderContent from "../common/components/render-content";
import ClientSelector from "../common/components/client-selector";
import SwitchDateFilters from "../common/components/switch-date-filters";

// Hooks
import {useAuth} from "../auth/auth";
import {useFilters} from "../filters/filters";
import {useUserClientsData} from "../user-clients-data/user-clients-data";

// Actions
import {readInvoices} from "./invoices-actions";
import {downloadPdf} from "../content/content-actions";
import {cleanDeliveryNotesState} from "../delivery-notes/delivery-notes-actions";
import {cleanTechnicalsSheetsState} from "../technical-sheets/technicals-sheets-actions";

// Selectors
import {invoicesSelector, invoicesIsFetchingSelector, invoicesErrorSelector} from "./invoices-selectors";
import {contentIsFetchingSelector, contentIdSelector, contentTypeSelector} from "../content/content-selectors";

// Component Selector
const invoicesPageSelector = createSelector(
    invoicesSelector,
    invoicesIsFetchingSelector,
    invoicesErrorSelector,
    contentIsFetchingSelector,
    contentIdSelector,
    contentTypeSelector,
    (invoices, invoicesIsFetching, invoicesError, contentIsFetching, contentId, contentType) => {
        return {
            invoices: invoices?.toJS() || [],
            invoicesIsFetching,
            invoicesError,
            contentIsFetching,
            contentId,
            contentType
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readInvoices: (filters) => dispatch(readInvoices(filters)),
        downloadPdf: (pdfId, pdfType, clientId) => dispatch(downloadPdf(pdfId, pdfType, clientId)),
        cleanDeliveryNotesState: () => dispatch(cleanDeliveryNotesState()),
        cleanTechnicalsSheetsState: () => dispatch(cleanTechnicalsSheetsState())
    }
};

const InvoicesPage = (props) => {

    const auth = useAuth();

    // filters
    const {
        invoicesFilters,
        dateFilters,
        setInvoicesFilters,
        setDateFilters,
        removeDeliveryNotesFilters,
        removeTechnicalSheetsFilters
    } = useFilters();

    // Client Selector
    const {selectedClient} = useAuth();
    const {userClientsData} = useUserClientsData();
    const selectedClientData = userClientsData.find(client => client._id === selectedClient);

    // Redux
    const {
        invoices,
        contentIsFetching, contentId, contentType
    } = useSelector(state => invoicesPageSelector(state));
    const {readInvoices, downloadPdf, cleanDeliveryNotesState, cleanTechnicalsSheetsState} = useReduxDispatch();

    // State
    const [documentId, setDocumentId] = useState(invoicesFilters?.documentId || "");
    const [fromDate, setFromDate] = useState(moment(dateFilters?.fromDate || null) || null);
    const [toDate, setToDate] = useState(moment(dateFilters?.toDate || null) || null);
    const [isManualRange, setIsManualRange] = useState(dateFilters?.isManualRange === true);
    const [predefinedRangeValue, setPredefinedRangeValue] = useState(dateFilters?.predefinedValue || "");

    //i18n
    const {t} = useTranslation("invoices");

    // state of showWelcomeMessage
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(!invoicesFilters);
    // state of date change
    const [isDateChanged, setIsDateChanged] = useState(false);

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold",
                        marginBottom: 10
                    }}>{t("title", { client: selectedClientData && selectedClientData.name } )}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                {auth.userType === "admin" && (
                    <ClientSelector />
                )}
                <TextInput
                    label={t("filters.invoiceNumber")}
                    style={{marginRight: 15, minWidth: 100, width: 130}}
                    value={documentId}
                    setValue={setDocumentId}
                />
                <SwitchDateFilters
                    fromValue={fromDate}
                    setFromValue={setFromDate}
                    toValue={toDate}
                    setToValue={setToDate}
                    isManualDateRange={isManualRange}
                    setIsManualDateRange={setIsManualRange}
                    predefinedValue={predefinedRangeValue}
                    setPredefinedValue={setPredefinedRangeValue}
                    setDateChanged={setIsDateChanged}
                />
                <Button
                    text={t("common:actions.search")}
                    action={() => {
                        const filters = {documentId};
                        // if isDateChange clean state and filters localStorage of delivery notes and technical sheets
                        if (isDateChanged) {
                            cleanDeliveryNotesState();
                            cleanTechnicalsSheetsState();
                            removeDeliveryNotesFilters();
                            removeTechnicalSheetsFilters();
                        }
                        readInvoices({
                            ...filters,
                            client: selectedClient,
                            documentId,
                            fromDate,
                            toDate
                        });
                        setInvoicesFilters(filters);
                        setDateFilters({
                            fromDate,
                            toDate,
                            isManualRange: isManualRange,
                            predefinedValue: predefinedRangeValue
                        });
                        setShowWelcomeMessage(false);
                    }}
                    intent="primary"
                />
            </div>
            <RenderContent 
                nameCollection={t("nameCollection")}
                data={invoices}
                showWelcomeMessage={showWelcomeMessage}
                renderHeader={false}
                config={{
                    sortable: true,
                    showFooter: true,
                    initialState: {sortBy: [{id: "date", desc: true}]}
                }}
                columns={[{
                    id: "date",
                    Header: t("invoicesTableColumns.date"),
                    accessor: "date",
                    type: "date"
                }, {
                    id: "document_id",
                    Header: t("invoicesTableColumns.document_id"),
                    accessor: "document_id"
                }, {
                    id: "base",
                    Header: t("invoicesTableColumns.base"),
                    accessor: "base",
                    type: "number"
                }, {
                    id: "vat",
                    Header: t("invoicesTableColumns.vat"),
                    accessor: "vat",
                    type: "number"
                }, {
                    id: "total",
                    Header: t("invoicesTableColumns.total"),
                    accessor: row =>
                        [row.base, row.vat].reduce(
                            (sum, current) => sum + current,
                            0
                        ),
                    type: "number"
                }, {
                    id: "actions",
                    Header: "",
                    accessor: "document_id",
                    type: "function",
                    disableSortBy: true,
                    minWidth: 60,
                    width: 60,
                    style: {
                        maxWidth: 60,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    },
                    nodeContent: (value) => {
                        return contentIsFetching && contentType === "invoice" && contentId === value ? (
                            <div>
                                <Spinner
                                    size={20}
                                    intent={"danger"}
                                />
                            </div>
                        ) : (
                            <div css={{
                                cursor: "pointer",
                                transition: "all 0.4s",
                                "&:hover": {
                                    color: primaryColor
                                }
                            }} onClick={() => downloadPdf(value, "invoice", selectedClient)}>
                                <Icon icon="download" iconSize={20} />
                            </div>
                        );
                    }
                }]}
            />
        </>
    );
};

export default InvoicesPage;